import React from "react";
import { useSelector } from "react-redux";
import { Col, Container, Row } from "react-bootstrap";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import Marquee from "react-fast-marquee";
import { STORAGE_URL } from "constants";
import { useLanguage } from "hooks";
import { Slider3d, Icons, Skeletons } from "components";
import ShowMore from "react-show-more";

function Hero() {
  const language = useLanguage(); //language returned from useLanguage hook; it’s an object with current language key --> "value in current language";
  const sliderRef = React.useRef(null);
  const { slides, news } = useSelector((state) => state.app); // call data from app store
  const [sliderIndex, setSliderIndex] = React.useState(0);

  // active item
  const [textItems, setTextItems] = React.useState(null);

  const next = () => {
    sliderRef.current.next();
  };

  const prev = () => {
    sliderRef.current.prev();
  };

  const renderArrows = () => (
    <div className="slider_arrow">
      <button className="arrow-btn prev" onClick={next}>
        <Icons.PinkLeft />
      </button>
      <button className="arrow-btn next" onClick={prev}>
        <Icons.PinkLeft />
      </button>
    </div> // slider arrows
  );

  React.useEffect(() => {
    let i = parseInt(sliderIndex);
    i = i !== 0 ? i : 0;
    setTextItems(slides.data[i]);
  }, [slides.data, sliderIndex]); // set textItems with the data in the slider active index

  React.useEffect(() => {
    const interval = setInterval(() => {
      next();
    }, 10000);
    return () => clearInterval(interval);
  }, [sliderIndex]);

  return (
    <section className="mb-3 hero">
      <Container>
        {slides.loading ? (
          <Skeletons.HeroSkeleton />
        ) : (
          slides.data.length > 0 && (
            <Row className="align-items-center mb-5">
              <Col xl={5} lg={6} xs={12} className="hero_content">
                {textItems && (
                  <TransitionGroup>
                    <CSSTransition
                      key={textItems.id}
                      timeout={300}
                      classNames="transition"
                    >
                      <div className="hero_container">
                        <Row key={textItems.id}>
                          {/** Slider Title */}
                          <Col xs={12}>
                            <h1 className="hero_title fw-medium text-primary cut-text-1">
                              {textItems.title}
                            </h1>
                          </Col>

                          {/** Slider Description */}
                          <Col xs={12} className="mb-3">
                            <ShowMore lines={2} anchorClass="d-none">
                              <p
                                className="hero_text text-light5"
                                dangerouslySetInnerHTML={{
                                  __html: textItems.text,
                                }}
                              />
                            </ShowMore>
                          </Col>

                          <Col xs={12}>
                            {textItems.link && (
                              <a
                                href={`${textItems.link}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary fw-bold hero_link"
                              >
                                {language.more}...
                              </a>
                            )}
                          </Col>
                        </Row>
                      </div>
                    </CSSTransition>
                    {/* transitions for the text; CSSTransition is the item to transition */}
                  </TransitionGroup>
                  // component from "react-transition-group" library to make transitions for the text; TransitionGroup is the container of effects
                )}
              </Col>
              <Col xl={2} xs={2}></Col>
              <Col
                xl={4}
                lg={4}
                xs={8}
                className="position-relative mt-4 mt-xlg-0"
              >
                <Slider3d
                  ref={sliderRef} // ref to control the slider
                  dir={language.direction} // slider direction ltr || rtl
                  items={slides.data.map((item, i) => (
                    <img src={`${STORAGE_URL}${item.image}`} alt={item.title} />
                  ))} // slider items list of nodes (jsx components)
                  onIdxChanged={setSliderIndex} // to have the current active index from the slider
                />
                {slides.data.length > 0 && renderArrows()} {/* slider arrows render if there a sliders */}
              </Col>
              <Col xl={2} xs={2}></Col>
            </Row>
          )
        )}
        {/* news bar */}
        <div className="mt-4 mt-4 mt-4 ">
          {news.loading && <Skeletons.NewsSkeleton />}
          {!news.loading && news.data.length > 0 && (
            // news Marquee
            <Marquee
              gradient={false}
              className="news-bar d-flex align-items-center bg-light shadow-0 rounded-2"
              pauseOnHover={true}
              direction={language.direction === "rtl" ? "right" : "left"}
              delay={0}
              speed={60}
            >
              {news.data.map((item, index) => {
                return (
                  <div
                    className={`${
                      index < news.data.length - 1 ? "border-end" : ""
                    } border-light5 px-2`}
                    key={index}
                  >
                    <h1
                      className={`h5 fw-bold mx-2 ${
                        index % 2 === 0 ? "text-primary" : "text-secondary"
                      }`}
                    >
                      <a
                        href={item.link}
                        className={`${
                          index % 2 === 0 ? "text-primary" : "text-secondary"
                        }`}
                      >
                        {item.title}
                      </a>
                    </h1>{" "}
                    <h6 className="fw-light text-light5">
                      {item.text}
                    </h6>
                  </div>
                );
              })}
            </Marquee>
          )}
        </div>
      </Container>
    </section>
  );
}

export default Hero;
